import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { SettingsProvider } from "@/context/SettingsContext";
import ScrollToTop from "@/components/ScrollToTop";
import StoreLayout from "@/components/StoreLayout";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import TrackOrder from "@/pages/TrackOrder";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import PolicyPage from "@/pages/PolicyPage";
import Categories from "@/pages/Categories";
import WishlistPage from "@/pages/WishlistPage";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import Account from "@/pages/Account";
import NotFound from "@/pages/NotFound";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminProductEdit from "@/pages/admin/AdminProductEdit";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import AdminNewsletter from "@/pages/admin/AdminNewsletter";
import AdminEnquiries from "@/pages/admin/AdminEnquiries";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminContent from "@/pages/admin/AdminContent";

function RequireAuth({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function RequireAdmin({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
    if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
    return children;
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <AuthProvider>
                    <SettingsProvider>
                        <CartProvider>
                            <WishlistProvider>
                                <ScrollToTop />
                                <Toaster position="top-center" richColors />
                                <Routes>
                                    <Route element={<StoreLayout />}>
                                        <Route path="/" element={<Home />} />
                                        <Route path="/shop" element={<Shop />} />
                                        <Route path="/categories" element={<Categories />} />
                                        <Route path="/products/:slug" element={<ProductDetail />} />
                                        <Route path="/cart" element={<Cart />} />
                                        <Route path="/checkout" element={<Checkout />} />
                                        <Route path="/order/:orderNumber" element={<OrderConfirmation />} />
                                        <Route path="/track" element={<TrackOrder />} />
                                        <Route path="/about" element={<About />} />
                                        <Route path="/contact" element={<Contact />} />
                                        <Route path="/faq" element={<FAQ />} />
                                        <Route path="/wishlist" element={<WishlistPage />} />
                                        <Route path="/policy/:key" element={<PolicyPage />} />
                                        <Route path="/login" element={<Login />} />
                                        <Route path="/register" element={<Register />} />
                                        <Route path="/forgot-password" element={<ForgotPassword />} />
                                        <Route path="/reset-password" element={<ResetPassword />} />
                                        <Route path="/auth/callback" element={<AuthCallback />} />
                                        <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
                                    </Route>

                                    <Route path="/admin/login" element={<AdminLogin />} />
                                    <Route path="/admin" element={<RequireAdmin><AdminLayout /></RequireAdmin>}>
                                        <Route index element={<AdminDashboard />} />
                                        <Route path="products" element={<AdminProducts />} />
                                        <Route path="products/new" element={<AdminProductEdit />} />
                                        <Route path="products/:pid" element={<AdminProductEdit />} />
                                        <Route path="orders" element={<AdminOrders />} />
                                        <Route path="reviews" element={<AdminReviews />} />
                                        <Route path="coupons" element={<AdminCoupons />} />
                                        <Route path="categories" element={<AdminCategories />} />
                                        <Route path="customers" element={<AdminCustomers />} />
                                        <Route path="newsletter" element={<AdminNewsletter />} />
                                        <Route path="enquiries" element={<AdminEnquiries />} />
                                        <Route path="content" element={<AdminContent />} />
                                        <Route path="settings" element={<AdminSettings />} />
                                    </Route>

                                    <Route path="*" element={<NotFound />} />
                                </Routes>
                            </WishlistProvider>
                        </CartProvider>
                    </SettingsProvider>
                </AuthProvider>
            </BrowserRouter>
        </div>
    );
}

export default App;
